import { IUserRepository } from '../../domain/repositories/IUserRepository';
import { User } from '../../domain/entities/User';
import { Email } from '../../domain/valueObjects/Email';
import { ConfigManager } from '../../application/config/Config';
import { LoggerFactory } from '../../application/logging/Logger';
import { NotFoundError, InternalServerError } from '../../application/errors/AppError';
import { SharetribeRepository } from '../../infrastructure/repositories/SharetribeRepository';
import { apiService } from '@/services';
import { storageService } from '../../application/services/StorageService';

/**
 * API-based User Repository Implementation
 * Follows Repository Pattern and Dependency Inversion Principle
 */
export class UserRepository implements IUserRepository {
  private readonly config = ConfigManager.getInstance().getApiConfig();
  private readonly logger = LoggerFactory.getLogger();
  private readonly sharetribeRepo = new SharetribeRepository(apiService); 


    /**
     * Fetch full user profile by user ID (legacy endpoint)
     * Mirrors backend: user/get_user/id/{user_id}
     */
    async getUserById(userId: number): Promise<any | null> {
      try {
        this.logger.debug("Fetching user by legacy ID endpoint", { userId });

        const response = await fetch(
          `${this.config.baseUrl}/user/get_user/id/${userId}`,
          {
            method: "GET",
            headers: {
              "Authorization": `Bearer ${storageService.getItem("auth_token")}`,
              "Accept": "*/*",
            },
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            return null;
          }
          throw new Error(`Failed to fetch user: ${response.statusText}`);
        }

        const json = await response.json();

        // Backend returns ProfileResponse-like object
        return json;

      } catch (error) {
        this.logger.error(
          "Error fetching user from legacy endpoint",
          error as Error,
          { userId }
        );
        return null;
      }
    }


  async getWeeklyPracticeSummary(date: string, userId: number) {
    const res = await fetch(
      `${this.config.baseUrl}/practice/weekly_summary`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${storageService.getItem("auth_token")}`
        },
        body: JSON.stringify({
          date,
          user_id: userId
        })
      }
    );

    if (!res.ok) {
      throw new Error("Failed to fetch weekly practice summary");
    }

    return res.json();
  }


  /**
   * Retrieves a user record by its ID
   * @param id - The unique identifier of the user record
   * @returns User domain entity or null if not found
   * @throws Error if the fetch operation fails
   */
  async findById(id: string): Promise<User | null> {
    try {
      this.logger.debug('Finding user by ID', { userId: id });
      
      const response = await fetch(`${this.config.baseUrl}/users/${id}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to find user: ${response.statusText}`);
      }

      const data = await response.json();
      return User.fromData(data.data);
    } catch (error) {
      this.logger.error('Error finding user by ID', error as Error, { userId: id });
      throw new NotFoundError(`User with ID ${id} not found`);
    }
  }

  /**
   * Retrieves a user record by email address
   * @param email - The email address value object
   * @returns User domain entity or null if not found
   * @throws Error if the fetch operation fails
   */
  async findByEmail(email: Email): Promise<User | null> {
    try {
      this.logger.debug('Finding user by email', { email: email.value });
      
      const response = await fetch(`${this.config.baseUrl}/users/email/${email.value}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to find user: ${response.statusText}`);
      }

      const data = await response.json();
      return User.fromData(data.data);
    } catch (error) {
      this.logger.error('Error finding user by email', error as Error, { email: email.value });
      throw new NotFoundError(`User with email ${email.value} not found`);
    }
  }

  /**
   * Creates a new user record
   * @param user - The User domain entity to save
   * @returns Created User domain entity with server-generated ID
   * @throws Error if the save operation fails
   */
  async save(user: User): Promise<User> {
    try {
      this.logger.debug('Saving user', { userId: user.id });
      
      const response = await fetch(`${this.config.baseUrl}/users`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(user.toJSON()),
      });

      if (!response.ok) {
        throw new Error(`Failed to save user: ${response.statusText}`);
      }

      const data = await response.json();
      return User.fromData(data.data);
    } catch (error) {
      this.logger.error('Error saving user', error as Error, { userId: user.id });
      throw error;
    }
  }

/* DELETE NOTIFICATION */
async deleteNotification(userId: number, notifId: number): Promise<boolean> {
  const form = new URLSearchParams();
  form.append("user_id", userId.toString());
  form.append("notif_id", notifId.toString());

  const res = await fetch(`${this.config.baseUrl}/user/delete_user_notification`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" , "Authorization": `Bearer ${storageService.getItem("auth_token")}` },
    body: form
  });

  return res.ok;
}

/* CLEAR NOTIFICATIONS */
async clearNotifications(userId: number): Promise<any[]> {
  const form = new URLSearchParams();
  form.append("user_id", userId.toString());

  const res = await fetch(`${this.config.baseUrl}/user/clear_user_notifications`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" ,"Authorization": `Bearer ${storageService.getItem("auth_token")}`},
    body: form
  });

  const json = await res.json();
  return JSON.parse(json.data);
}

/* ACCEPT COACH CONNECTION FROM SWIMMER */
async acceptCoachConnectionFromSwimmer(coachId: number, userId: number, accept: boolean) {
  return this.postAction("/coaches/accept_coach_connection_from_swimmer", {
    coach_id: coachId,
    user_id: userId,
    action: accept ? "1" : "2",
  });
}

/* ACCEPT COACH CONNECTION FROM COACH */
async acceptCoachConnectionFromCoach(userId: number, coachId: number, accept: boolean) {
  return this.postAction("/coaches/accept_coach_connection_from_coach", {
    user_id: userId,
    coach_id: coachId,
    action: accept ? "1" : "2",
  });
}

/* ACCEPT JOIN TEAM */
async acceptJoinTeam(userId: number, teamId: number, accept: boolean) {
  return this.postAction("/teams/accept_join_team", {
    user_id: userId,
    team_id: teamId,
    action: accept ? "1" : "2",
  });
}

/* ACCEPT SWIMMER CONNECTION */
async acceptSwimmerConnection(userId: number, friendId: number, accept: boolean) {
  return this.postAction("/user/accept_swimmer_connection", {
    user_id: userId,
    friend_id: friendId,
    action: accept ? "1" : "2",
  });
}

/* ACCEPT INVITE TEAM */
async acceptInviteTeam(userId: number, coachId: number, teamId: number, accept: boolean) {
  return this.postAction("/teams/accept_invite_team", {
    user_id: userId,
    coach_id: coachId,
    team_id: teamId,
    action: accept ? "1" : "2",
  });
}

/* GENERIC POST helper */
private async postAction(endpoint: string, body: any): Promise<number> {
  const form = new URLSearchParams();
  Object.keys(body).forEach(k => form.append(k, body[k]));

  const res = await fetch(`${this.config.baseUrl}${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" , "Authorization": `Bearer ${storageService.getItem("auth_token")}`,},
    body: form
  });

  const json = await res.json();
  return json.status;
}



/**
 * Fetches notifications for a given user
 * @param userId - Unique identifier of the user
 * @returns List of TeamNotification objects
 */
async fetchNotifications(userId: number): Promise<any[]> {
  try {
    this.logger.debug("Fetching user notifications", { userId });

    const formBody = new URLSearchParams();
    formBody.append("user_id", userId.toString());

    const response = await fetch(
      `${this.config.baseUrl}/user/fetch_user_notifications`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Authorization": `Bearer ${storageService.getItem("auth_token")}`,
        },
        body: formBody.toString(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch notifications: ${response.statusText}`);
    }

    const result = await response.json();

    // result.data is a string containing JSON → parse it again
    const notifications = JSON.parse(result.data);

    return notifications;
  } catch (error) {
    this.logger.error(
      "Error fetching notifications",
      error as Error,
      { userId }
    );

    // Return empty array like your C# code
    return [];
  }
}


  /**
   * Updates user profile information
   * @param params - Object containing user profile data to update
   * @param params.id - User ID
   * @param params.dob - Date of birth
   * @param params.gender - Gender
   * @param params.height - Height in cm
   * @param params.weight - Weight in kg
   * @param params.shoesize - Shoe size (optional)
   * @param params.handsize - Hand size (optional)
   * @param params.lang - Language preference
   * @param params.fname - First name
   * @param params.lname - Last name
   * @param params.image - Profile image URL or base64
   * @param params.third_party_swimmer_id - Third party swimmer ID (optional)
   * @returns Updated User domain entity
   * @throws Error if the update operation fails
   */
  async updateProfile(params: {
  id: number;
  dob: string;
  gender: string;
  height: number;
  weight: number;
  shoesize?: number;
  handsize?: number;
  lang: string;
  fname: string;
  lname: string;
  image: string;
  third_party_swimmer_id?: string;
}): Promise<User> {
  try {
    this.logger.debug("Updating profile", { userId: params.id });

    if (!params.id) throw new Error("Missing or invalid user ID");

    const formData = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });

    const response = await fetch(`${this.config.baseUrl}/user/update_profile`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${storageService.getItem("auth_token")}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: formData.toString(),
    });

    if (!response.ok) {
      throw new Error(`Failed to update profile: ${response.statusText}`);
    }

    const data = await response.json();
    if (data.status === 0) {
      throw new InternalServerError(`Profile update failed: ${data.msg || "Unknown API error"}`);
    }

    const updatedUser = User.fromData(data.data);

    //  UPDATE PROFILE IN SHARETRIBE

    try {
      const publicData: Record<string, any> = {
        type: updatedUser.type,
        user_id: String(updatedUser.id),
      };

      if (params.third_party_swimmer_id) {
        publicData["third_party_swimmer_id"] = params.third_party_swimmer_id;
      }

      const privateData: Record<string, any> = {
        dob: params.dob,
        gender: params.gender,
        lang: params.lang,
        height: String(params.height),
        weight: String(params.weight),
      };

      await this.sharetribeRepo.updateProfile(params.fname,
        params.lname,
        null,
        null,
        publicData,
        null,
        privateData,
        null
      );

      this.logger.info("Sharetribe profile updated successfully", {
        swimnerdUserId: params.id,
      });

    } catch (sharetribeError) {
      this.logger.warn("Sharetribe profile update failed");
    }

    return updatedUser;

  } catch (error) {
    this.logger.error("Error updating profile", error as Error, { userId: params.id });
    throw error;
  }
}


  /**
   * Deletes a user account
   * Note: Uses POST method instead of DELETE because the backend API endpoint `/login/delete` requires POST
   * @param id - The unique identifier of the user to delete
   * @throws Error if the delete operation fails
   */
async deleteWithPassword(userId: string, userPassword: string): Promise<void> {
  this.logger.debug("Deleting user account", { userId });

  // STEP 1: Get padded ID password
  const numericId = parseInt(String(userId).replace(/\D/g, "").slice(0, 8)) || 0;
  const paddedIdPassword = numericId.toString().padStart(8, "0");

  // STEP 2: Delete from Sharetribe first
  try {
    await this.sharetribeRepo.deleteCurrentUser(paddedIdPassword);
    this.logger.info("Sharetribe account deleted");
  } catch (e) {
    this.logger.warn("Sharetribe delete failed");
  }

  // STEP 3: Delete from SwimNerd API
  const formData = new URLSearchParams();
  formData.append("user_id", userId);
  formData.append("password", userPassword);

  const response = await fetch(`${this.config.baseUrl}/login/delete`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${storageService.getItem("auth_token")}`,
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Failed to delete account from Swimnerd");
  }

  this.logger.info("Swimnerd user deleted", { userId });
}


  async exists(email: Email): Promise<boolean> {
    try {
      this.logger.debug('Checking if user exists', { email: email.value });
      
      const response = await fetch(`${this.config.baseUrl}/users/exists/${email.value}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.exists || false;
    } catch (error) {
      this.logger.error('Error checking if user exists', error as Error, { email: email.value });
      return false;
    }
  }

  private getHeaders(): Record<string, string> {
    const token = storageService.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Accept': '*/*',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }
}
